import { useMemo } from 'react';
import useQMProject from 'react-scale/corp/hooks/qm/useQMProject';
import { useConfigData } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { useUser } from 'react-scale/components/ui/UserContext';
import { ConfigType, DiversityMetricsConfig, TaskType } from '@scale/shared/defaults';
import { IConfigMeta } from 'react-scale/corp/components/JsonSchemaConfigs/configTransforms';
import { useConfigAttributes } from 'react-scale/corp/components/JsonSchemaConfigs/useConfigAttributes';
import { IProjectJson } from '@scale/shared/types/project';

/** Fetch the Diversity Project Config for the project
 * @param projectId - The project ID to fetch the config for.
 */
export const useDiversityConfig = (projectId: string | undefined) => {
  const user = useUser();

  // ---------------- Get the Diversity Project Config ----------------
  const { project } = useQMProject(projectId);
  const projectTaskType = useMemo<TaskType | undefined>(() => project?.taskType, [project]);

  const configMeta = useMemo<IConfigMeta>(
    () => ({
      type: ConfigType.Project,
      id: projectId,
      taskType: projectTaskType,
    }),
    [projectId, projectTaskType],
  );

  const projectJson = project as unknown as IProjectJson | undefined;
  const configAttributes = useConfigAttributes(configMeta, projectJson, user);

  const {
    data: configData,
    isLoading: isConfigLoading,
    refetch,
  } = useConfigData({
    configMeta,
    configAttributes,
  });

  return {
    isLoading: isConfigLoading,
    refetch,
    diversityMetricsConfig: configData?.config?.diversityMetricsConfig as
      | DiversityMetricsConfig
      | undefined,
  };
};
